import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import TerminalProgress from '../components/progress-styles/TerminalProgress';
import GlassProgress from '../components/progress-styles/GlassProgress';
import MinimalProgress from '../components/progress-styles/MinimalProgress';
import NeonProgress from '../components/progress-styles/NeonProgress';

/**
 * Demo page to compare all progress indicator styles
 */
const ProgressStyleDemo = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(3);

  const styles = [
    { name: 'Terminal/Command Prompt', component: TerminalProgress, bg: 'bg-slate-950' },
    { name: 'Glassmorphism', component: GlassProgress, bg: 'bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900' },
    { name: 'Minimalist/Flat', component: MinimalProgress, bg: 'bg-slate-950' },
    { name: 'Neon/Cyberpunk', component: NeonProgress, bg: 'bg-black' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-xl font-bold">Progress Indicator Style Options</h1>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">Current Step:</span>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5, 6].map((step) => (
                  <button
                    key={step}
                    onClick={() => setCurrentStep(step)}
                    className={`w-8 h-8 rounded ${
                      currentStep === step
                        ? 'bg-blue-500 text-white'
                        : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
                    } transition-colors text-sm font-medium`}
                  >
                    {step}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Demo Grid */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-2 gap-6">
          {styles.map((style, index) => {
            const ProgressComponent = style.component;
            return (
              <div key={index} className="space-y-3">
                {/* Style Name */}
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">{style.name}</h2>
                  <span className="text-xs text-gray-500">Option {index + 1}</span>
                </div>

                {/* Preview Box */}
                <div className={`${style.bg} rounded-xl border border-slate-800 overflow-hidden`}>
                  <div className="relative h-[400px] p-8">
                    {/* Background pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                        backgroundSize: '40px 40px'
                      }}></div>
                    </div>

                    {/* Progress Component */}
                    <div className="relative">
                      <ProgressComponent currentStep={currentStep} />
                    </div>

                    {/* Hover Instruction */}
                    <div className="absolute bottom-4 left-4 right-4 text-center">
                      <p className="text-xs text-gray-500">
                        Hover over the indicator to see expanded view
                      </p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="text-xs text-gray-400 space-y-1 px-2">
                  {index === 0 && (
                    <>
                      <p>• Retro terminal aesthetic</p>
                      <p>• Monospace font, green text</p>
                      <p>• Command prompt styling</p>
                    </>
                  )}
                  {index === 1 && (
                    <>
                      <p>• Modern glassmorphism effect</p>
                      <p>• Heavy blur and gradients</p>
                      <p>• Contemporary look</p>
                    </>
                  )}
                  {index === 2 && (
                    <>
                      <p>• Clean, simple design</p>
                      <p>• No effects or blur</p>
                      <p>• Minimal resource usage</p>
                    </>
                  )}
                  {index === 3 && (
                    <>
                      <p>• Cyberpunk/futuristic style</p>
                      <p>• Neon glow effects</p>
                      <p>• High contrast colors</p>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Implementation Note */}
        <div className="mt-8 p-6 bg-blue-900/20 border border-blue-800/30 rounded-xl">
          <h3 className="text-sm font-semibold text-blue-400 mb-2">Implementation Note</h3>
          <p className="text-xs text-gray-400">
            All options have the same functionality: compact by default, expand on hover.
            To use your preferred style, simply replace the import in Dashboard.jsx:
            <code className="block mt-2 p-2 bg-black/40 rounded text-green-400 font-mono">
              import FloatingProgressPill from './components/progress-styles/[YourChoice]Progress';
            </code>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProgressStyleDemo;
